/**
 * 해시 라우팅 유틸리티
 * 모달 열기/닫기 시 URL 해시를 관리합니다.
 */

export type ModalType = 'token' | 'social' | 'note';

/**
 * 현재 경로를 가져옵니다.
 * @returns 현재 경로 ('/' 또는 '/social')
 */
export const getCurrentPath = (): string => {
  return window.location.pathname;
};

/**
 * 모달 해시를 생성합니다.
 * @param tokenAddress 토큰 주소
 * @param modalType 모달 타입 ('token' | 'social')
 * @returns 해시 문자열 (예: '/{tokenAddress}/token')
 */
export const createModalHash = (tokenAddress: string, modalType: ModalType): string => {
  return `/${tokenAddress}/${modalType}`;
};

/**
 * 현재 해시를 파싱합니다.
 * @returns { tokenAddress: string, modalType: ModalType } | null
 */
export const parseHash = (): { tokenAddress: string; modalType: ModalType } | null => {
  const hash = window.location.hash;
  if (!hash || hash.length <= 1) {
    return null;
  }

  // '#' 제거
  const hashWithoutPrefix = hash.substring(1);
  
  // '/'로 시작하는지 확인
  if (!hashWithoutPrefix.startsWith('/')) {
    return null;
  }

  // '/'로 분리
  const parts = hashWithoutPrefix.split('/').filter(p => p);
  
  if (parts.length !== 2) {
    return null;
  }

  const [tokenAddress, modalType] = parts;
  
  if (modalType !== 'token' && modalType !== 'social' && modalType !== 'note') {
    return null;
  }

  return {
    tokenAddress,
    modalType: modalType as ModalType
  };
};

/**
 * 해시를 설정합니다.
 * @param tokenAddress 토큰 주소
 * @param modalType 모달 타입
 */
export const setHash = (tokenAddress: string, modalType: ModalType): void => {
  const hash = createModalHash(tokenAddress, modalType);
  window.location.hash = hash;
};

/**
 * 해시를 제거합니다.
 */
export const clearHash = (): void => {
  // 해시를 제거하기 위해 history API 사용
  if (window.history.replaceState) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } else {
    // fallback for older browsers
    window.location.hash = '';
  }
};

/**
 * 모달을 열 때 해시를 설정합니다.
 * 현재 경로에 따라 적절한 해시를 생성합니다.
 * @param tokenAddress 토큰 주소
 * @param modalType 모달 타입
 */
export const openModalWithHash = (tokenAddress: string, modalType: ModalType): void => {
  setHash(tokenAddress, modalType);
};

/**
 * 모달을 닫을 때 해시를 제거합니다.
 */
export const closeModalWithHash = (): void => {
  clearHash();
};

